/**
 * Recover from stale-deploy asset failures.
 *
 * Every deploy replaces the content-hashed chunk files under /assets/, so a tab
 * loaded before the deploy 404s when it later fetches a chunk it hasn't loaded
 * yet (dynamic imports and their CSS). Vite's preload helper dispatches
 * `vite:preloadError` on window for exactly these failures — a one-shot reload
 * picks up the new build and self-heals the session.
 *
 * Guards:
 * - At most one reload per minute (sessionStorage timestamp), so a genuinely
 *   persistent failure — offline, blocked CDN — surfaces as an error in Sentry
 *   instead of reload-looping.
 * - When sessionStorage is unavailable (Safari private mode), fall back to at
 *   most one reload per page lifetime via a module-level flag.
 *
 * Deliberately does NOT call `event.preventDefault()`: with preventDefault the
 * failed import resolves `undefined`, so callers that destructure the module
 * (e.g. the lazy `import('@/lib/refreshUser')` in the billing store) would throw
 * a confusing secondary TypeError before the reload lands. Letting the original
 * error propagate once per stale deploy is harmless and self-heals on reload.
 */
const RELOAD_GUARD_KEY = 'vitePreloadErrorReloadedAt';
const RELOAD_GUARD_WINDOW_MS = 60_000;

/** Fallback guard when sessionStorage is unavailable: one reload per page lifetime. */
let reloadedThisPageLifetime = false;

export function installPreloadErrorReload(win: Window = window): void {
  win.addEventListener('vite:preloadError', () => {
    if (shouldReload(win)) win.location.reload();
  });
}

function shouldReload(win: Window): boolean {
  try {
    const lastReloadAt = Number(win.sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
    if (Date.now() - lastReloadAt < RELOAD_GUARD_WINDOW_MS) return false;
    win.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    return true;
  } catch {
    if (reloadedThisPageLifetime) return false;
    reloadedThisPageLifetime = true;
    return true;
  }
}
